import { Component, OnInit } from '@angular/core';
import { AccountService } from 'src/app/core/services/account.service';
import { lastValueFrom, take } from 'rxjs';
import { Router } from '@angular/router';
import { MemoryStorageService } from 'src/app/core/services/memory-storage.service';
import { User } from 'src/app/core/models/user';
import { NgxSpinnerService } from 'ngx-spinner';
import swal from 'sweetalert2';

interface Plan {
  id: string;
  plan_id: string;
  plan_name: string;
  tenor: number;
  roi: number;
  emi_amount: number;
  navigate_url: string;
}

interface LenderData {
  status: string;
  continue: boolean;
  lender: string;
  lenderId: string;
  plans: Plan[];
  logo: string;
  loanAmount: number;
  lenderCode: string;
  inprincipalAmount: string;
  penalCharges: string;
  creditScore: string;
  isEnabled: boolean;
  selectedTenure: number;
}

@Component({
  selector: 'app-lender-selection',
  templateUrl: './lender-selection.component.html',
  styleUrl: './lender-selection.component.scss'
})
export class LenderSelectionComponent implements OnInit {
  constructor(private apiService: AccountService, private router: Router, private spinner: NgxSpinnerService) { }
  async ngOnInit() {
    const customerToken = MemoryStorageService.getItem('customerToken');
    if (customerToken) {
      this.spinner.show();
      try {
        const plans = await lastValueFrom(this.apiService.GetLenderSelection(customerToken));

        if (plans['data']) {
          this.approvedLenders = plans['data'].filter(l => l.status === 'Approved');
          this.rejectedLenders = plans['data'].filter(l => l.status !== 'Approved');
          console.log(plans);

          if (this.approvedLenders.length === 0) {
            this.router.navigateByUrl('/assist-journey/sorry');
          }
        } else {
          if (plans['error'] === 'No plan generated') {
            this.router.navigateByUrl('/assist-journey/assist-underwrite');
          }
          else if (plans['error'] === 'Invalid lender') {
            this.router.navigateByUrl('/assist-journey/session-expired');
          }
        }

      } catch (error: any) {
        if (error.status === 0) {
          swal.fire("No internet connection. Please check your network and try again.");
          return;
        }
        if (error?.status === 401 && error?.error?.message === 'Token has expired') {
          // Redirect to /abc if token expired
          this.router.navigateByUrl('/assist-journey/session-expired');
        } else {
          // Handle other errors (optional)
          console.error('API Error:', error);
        }
      } finally {
        this.spinner.hide();
      }
    } else {
      this.router.navigateByUrl('/assist-journey/session-expired');
    }
  }


  approvedLenders: LenderData[] = [];
  rejectedLenders: LenderData[] = [];


  getTenureRange(plans: Plan[]): string {
    const tenors = plans.map(p => p.tenor);
    const min = Math.min(...tenors);
    const max = Math.max(...tenors);
    return min === max ? `${min} month` : `${min} to ${max} months`;
  }

  getMinEmi(plans: any[]) {
    if (!plans || plans.length === 0) return 0;
    // Filter plans that are eligible and have emi_amount
    const eligibleEmis = plans
      .filter(p => p.plan_eligibility && p.emi_amount)
      .map(p => p.emi_amount);
    return Math.min(...eligibleEmis).toFixed(0);
  }

  getInterestRange(plans: Plan[]): string {
    if (!plans || plans.length === 0) return '-';
    const rois = plans.map(p => p.roi);
    const min = Math.min(...rois);
    const max = Math.max(...rois);
    return min === max ? `${min}%` : `${min}% to ${max}%`;
  }

  getMinProcessingFee(plans: any[]): number {
    return Math.min(...plans.map(p => p.processing_fee));
  }

  getLoanAmountRange(plans: any[]): string {
    const amounts = plans.map(p => p.loan_amount);
    const min = Math.min(...amounts);
    const max = Math.max(...amounts);
    return min === max ? `${min}` : `${min} to ${max}`;
  }

  // Approx Annual Percentage Rate (APR) calculation
  getAprRange(plans: any[]): string {
    const aprs = plans.map(p => {
      const monthlyRate = p.roi / 100; // assuming ROI given monthly
      const annualRate = (Math.pow(1 + monthlyRate, 12) - 1) * 100;
      return annualRate;
    });
    const min = Math.min(...aprs);
    const max = Math.max(...aprs);
    return min === max
      ? `${min.toFixed(2)}%`
      : `${min.toFixed(2)}% to ${max.toFixed(2)}%`;
  }



  continueApplication(lender: LenderData) {
    var url = lender.plans.find(r => r.tenor === lender.selectedTenure)?.navigate_url;
    if (url) {
      window.location.href = url;
    }
    else {
      this.router.navigateByUrl('/assist-journey/session-expired');
    }
  }

  SelectLender(lender: LenderData) {
    this.spinner.show();
    MemoryStorageService.setItem('selectedLenderId', lender.lenderId);
    MemoryStorageService.setItem('assistTravelPlans', JSON.stringify(lender.plans));
    MemoryStorageService.setItem('loanAmount', JSON.stringify(lender.loanAmount));
    MemoryStorageService.setItem('approvalAmount', lender.inprincipalAmount);
    MemoryStorageService.setItem('creditscore', lender.creditScore);
    const user: User = { token: MemoryStorageService.getItem('customerToken'), userName: '', email: '', mobileNumber: '' }
    this.apiService.setCurrentUser(user);
    this.apiService.currentUserSource.pipe(take(1)).subscribe(() => {
      this.apiService.setMoengageUser(user.token);
    });
    this.spinner.hide();
    this.router.navigateByUrl('/assist-journey/payment-plans/' + lender.lenderCode);
  }

}
